import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, Banknote, Building2, Calculator, FilePlus, FolderInput, FolderPlus, History, MoreVertical, Pencil, RotateCcw, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator,
  DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { isCellEditable, type BudgetAffordances } from '../lib/permissions'
import { placementProblem, siblingPosition, type CostTree } from '../lib/tree'
import type { CostBuildingRef, CostRow } from '../types'

export type RowAction =
  | { kind: 'addGroup' }
  | { kind: 'addItem' }
  | { kind: 'rename' }
  | { kind: 'editBudget' }
  | { kind: 'moveUp' }
  | { kind: 'moveDown' }
  | { kind: 'moveTo' }
  | { kind: 'linkBuilding'; buildingId: string | null }
  | { kind: 'clearForecast' }
  | { kind: 'groupEstimate' }
  | { kind: 'delete' }
  | { kind: 'history' }

const NONE = '__none__'

interface Props {
  row: CostRow
  tree: CostTree
  affordances: BudgetAffordances
  buildings: CostBuildingRef[]
  onAction: (nodeId: string, action: RowAction) => void
}

/** Offers only what the permissions and the tree rules allow (placementProblem mirrors the server). */
const RowMenu: React.FC<Props> = ({ row, tree, affordances: af, buildings, onAction }) => {
  const { t } = useTranslation()
  // An inline editor opens only once the menu has closed: the open menu traps the focus and the closing one hands it
  // back to its trigger, so an editor mounted earlier never gets the caret.
  const pendingEdit = useRef<RowAction | null>(null)
  const act = (action: RowAction) => {
    if (action.kind === 'rename' || action.kind === 'editBudget') pendingEdit.current = action
    else onAction(row.id, action)
  }
  const group = row.kind === 'GROUP'
  // A group takes new rows inside; an item gets a new sibling right after it.
  const addParent = group ? row.id : row.parentId
  const canAddGroup = af.addNode && group && !placementProblem(tree, 'GROUP', row.id)
  const canAddItem = af.addNode && addParent !== null && !placementProblem(tree, 'ITEM', addParent)
  const canLink = af.linkBuilding && row.parentId === null
  const canClear = af.editForecast && row.hasForecastOverride
  const canGroupEstimate = af.editForecast && group
  // The cell itself edits on a double click, which nobody finds; the menu says it out loud.
  const canEditBudget = isCellEditable(row, 'originalBudget', af)
  // "Historie" is for everyone who sees the budget, so the menu never disappears.
  const anyEdit = canAddGroup || canAddItem || af.editName || canEditBudget || af.move || canLink || canClear || canGroupEstimate || af.deleteNode
  // The sections before them already end the menu's first block, so a separator goes in only when an edit item sits above.
  const precedesMove = canAddGroup || canAddItem || af.editName || canEditBudget
  const precedesDelete = precedesMove || af.move || canLink || canClear || canGroupEstimate
  const { index, count } = siblingPosition(tree, row.id)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-6 w-6 shrink-0 p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100" aria-label={t('CostControl_row_menu')}>
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onCloseAutoFocus={(e) => {
          const edit = pendingEdit.current
          if (!edit) return
          pendingEdit.current = null
          e.preventDefault()
          onAction(row.id, edit)
        }}>
        <DropdownMenuItem onClick={() => act({ kind: 'history' })}><History className="h-4 w-4 mr-2" />{t('CostControl_node_history')}</DropdownMenuItem>
        {anyEdit && <DropdownMenuSeparator />}
        {canAddGroup && <DropdownMenuItem onClick={() => act({ kind: 'addGroup' })}><FolderPlus className="h-4 w-4 mr-2" />{t('CostControl_add_subgroup')}</DropdownMenuItem>}
        {canAddItem && <DropdownMenuItem onClick={() => act({ kind: 'addItem' })}><FilePlus className="h-4 w-4 mr-2" />{t(group ? 'CostControl_add_item' : 'CostControl_add_item_after')}</DropdownMenuItem>}
        {af.editName && <DropdownMenuItem onClick={() => act({ kind: 'rename' })}><Pencil className="h-4 w-4 mr-2" />{t('CostControl_rename')}</DropdownMenuItem>}
        {canEditBudget && <DropdownMenuItem onClick={() => act({ kind: 'editBudget' })}><Banknote className="h-4 w-4 mr-2" />{t('CostControl_edit_budget')}</DropdownMenuItem>}
        {af.move && (
          <>
            {precedesMove && <DropdownMenuSeparator />}
            <DropdownMenuItem disabled={index <= 0} onClick={() => act({ kind: 'moveUp' })}><ArrowUp className="h-4 w-4 mr-2" />{t('CostControl_move_up')}</DropdownMenuItem>
            <DropdownMenuItem disabled={index >= count - 1} onClick={() => act({ kind: 'moveDown' })}><ArrowDown className="h-4 w-4 mr-2" />{t('CostControl_move_down')}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => act({ kind: 'moveTo' })}><FolderInput className="h-4 w-4 mr-2" />{t('CostControl_move_to')}</DropdownMenuItem>
          </>
        )}
        {canLink && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger><Building2 className="h-4 w-4 mr-2" />{t('CostControl_link_building')}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={row.buildingId ?? NONE} onValueChange={(v) => act({ kind: 'linkBuilding', buildingId: v === NONE ? null : v })}>
                <DropdownMenuRadioItem value={NONE}>{t('CostControl_no_building')}</DropdownMenuRadioItem>
                {buildings.map((b) => <DropdownMenuRadioItem key={b.id} value={b.id}>{b.name}</DropdownMenuRadioItem>)}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        {canClear && <DropdownMenuItem onClick={() => act({ kind: 'clearForecast' })}><RotateCcw className="h-4 w-4 mr-2" />{t('CostControl_clear_forecast')}</DropdownMenuItem>}
        {canGroupEstimate && <DropdownMenuItem onClick={() => act({ kind: 'groupEstimate' })}><Calculator className="h-4 w-4 mr-2" />{t('CostControl_estimate_group_action')}</DropdownMenuItem>}
        {af.deleteNode && (
          <>
            {precedesDelete && <DropdownMenuSeparator />}
            <DropdownMenuItem className="text-destructive" onClick={() => act({ kind: 'delete' })}><Trash2 className="h-4 w-4 mr-2" />{t('Common_delete')}</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
export default RowMenu
